import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { authApi, setToken, setRefreshToken, clearToken, jwtRole, getStoredAuth, type RegisterRequest, rolesApi, usersApi } from "@/lib/api";

export type AdminRole =
  | "super_admin"
  | "venue_manager"
  | "hotel_owner"
  | "event_centre_owner"
  | "open_space_owner"
  | "resort_owner"
  | "hotel_event_owner"
  | "apartment_owner"
  | "conference_hall_owner"
  | "moderator";

export type Permission =
  | "venues.manage_all"
  | "venues.manage_assigned"
  | "venues.moderate"
  | "venues.feature"
  | "venues.approve"
  | "bookings.manage"
  | "bookings.view"
  | "users.manage"
  | "users.view"
  | "admins.manage"
  | "settings.manage"
  | "reports.access";

const DEFAULT_ROLE_PERMS: Record<AdminRole, Permission[]> = {
  super_admin: [
    "venues.moderate", "venues.feature", "venues.approve",
    "bookings.manage", "bookings.view",
    "users.manage", "users.view", "admins.manage",
    "settings.manage", "reports.access",
  ],
  venue_manager: [
    "venues.manage_assigned", "bookings.view",
  ],
  hotel_owner: [
    "venues.manage_assigned", "bookings.view",
  ],
  event_centre_owner: [
    "venues.manage_assigned", "bookings.view",
  ],
  open_space_owner: [
    "venues.manage_assigned", "bookings.view",
  ],
  resort_owner: [
    "venues.manage_assigned", "bookings.view",
  ],
  hotel_event_owner: [
    "venues.manage_assigned", "bookings.view",
  ],
  apartment_owner: [
    "venues.manage_assigned", "bookings.view",
  ],
  conference_hall_owner: [
    "venues.manage_assigned", "bookings.view",
  ],
  moderator: [
    "venues.moderate", "venues.approve", "bookings.view", "users.view",
  ],
};

export const ROLE_LABEL: Record<AdminRole, string> = {
  super_admin: "Super Admin",
  venue_manager: "Venue Manager",
  hotel_owner: "Hotel Owner",
  event_centre_owner: "Event Centre Owner",
  open_space_owner: "Open Space Owner",
  resort_owner: "Resort Owner",
  hotel_event_owner: "Hotel & Event Owner",
  apartment_owner: "Apartment Owner",
  conference_hall_owner: "Conference Hall Owner",
  moderator: "Moderator",
};

export interface User {
  id: string;
  name: string;
  email: string;
  role?: AdminRole | null;
}

export type { RegisterRequest };

const BUSINESS_OWNER_ROLES: AdminRole[] = [
  "venue_manager", "hotel_owner", "event_centre_owner",
  "open_space_owner", "resort_owner", "hotel_event_owner",
  "apartment_owner", "conference_hall_owner",
];

interface AuthCtx {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isVenueManager: boolean;
  isBusinessOwner: boolean;
  isModerator: boolean;
  role: AdminRole | null;
  hasPermission: (p: Permission) => boolean;
  login: (email: string, password: string) => Promise<User>;
  signup: (data: RegisterRequest) => Promise<User>;
  logout: () => void;
  rolePerms: Record<AdminRole, Permission[]>;
  updateRolePermissions: (role: AdminRole, perms: Permission[]) => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [rolePerms, setRolePermsState] = useState<Record<AdminRole, Permission[]>>(DEFAULT_ROLE_PERMS);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const auth = getStoredAuth();
    if (auth) {
      setUser({ id: auth.id, name: auth.name, email: auth.email, role: auth.role as AdminRole | null });
    }
  }, []);

  // Fetch all role permissions from backend when SuperAdmin is logged in
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (user?.role !== "super_admin") return; // endpoint requires SuperAdmin
    const allRoles: AdminRole[] = [
      "super_admin", "venue_manager", "hotel_owner", "event_centre_owner",
      "open_space_owner", "resort_owner", "hotel_event_owner",
      "apartment_owner", "conference_hall_owner", "moderator",
    ];
    Promise.all(
      allRoles.map(async (r) => {
        try {
          const perms = await rolesApi.getPermissions(r);
          return { role: r, perms: perms as Permission[] };
        } catch {
          return { role: r, perms: DEFAULT_ROLE_PERMS[r] };
        }
      })
    ).then(results => {
      const next: Record<AdminRole, Permission[]> = { ...DEFAULT_ROLE_PERMS };
      for (const { role, perms } of results) {
        next[role] = perms;
      }
      setRolePermsState(next);
    });
  }, [user?.role, user?.id]);

  const persist = (u: User, accessToken: string, refreshToken?: string) => {
    setToken(accessToken);
    if (refreshToken) setRefreshToken(refreshToken);
    setUser(u);
  };

  const login = async (email: string, password: string): Promise<User> => {
    const res = await authApi.login({ email, password });
    const backendRole = jwtRole(res.accessToken);
    const resolvedRole: AdminRole | null =
      backendRole === "SuperAdmin" ? "super_admin" :
      backendRole === "VenueManager" ? "venue_manager" :
      backendRole === "HotelOwner" ? "hotel_owner" :
      backendRole === "EventCentreOwner" ? "event_centre_owner" :
      backendRole === "OpenSpaceOwner" ? "open_space_owner" :
      backendRole === "ResortOwner" ? "resort_owner" :
      backendRole === "HotelEventOwner" ? "hotel_event_owner" :
      backendRole === "ApartmentOwner" ? "apartment_owner" :
      backendRole === "ConferenceHallOwner" ? "conference_hall_owner" :
      backendRole === "Moderator" ? "moderator" :
      null;
    const u: User = {
      id: res.user.id,
      name: `${res.user.firstName} ${res.user.lastName}`.trim(),
      email: res.user.email ?? email,
      role: resolvedRole,
    };
    persist(u, res.accessToken, res.refreshToken);
    return u;
  };

  const signup = async (data: RegisterRequest): Promise<User> => {
    await authApi.register(data);
    return login(data.email, data.password);
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  const updateRolePermissions = async (roleToUpdate: AdminRole, newPerms: Permission[]) => {
    const previous = rolePerms[roleToUpdate] || [];
    setRolePermsState(prev => ({ ...prev, [roleToUpdate]: newPerms }));
    try {
      await rolesApi.updatePermissions(roleToUpdate, newPerms);
    } catch {
      // Revert on error
      setRolePermsState(prev => ({ ...prev, [roleToUpdate]: previous }));
      throw new Error("Failed to save permission change.");
    }
  };

  const role = user?.role ?? null;
  const perms = role ? rolePerms[role] || [] : [];
  const isBusinessOwner = role ? BUSINESS_OWNER_ROLES.includes(role) : false;
  const isModerator = role === "moderator";
  const isSuperAdmin = role === "super_admin";
  const hasPermission = (p: Permission) => isSuperAdmin || perms.includes(p);
  const isAdmin = isSuperAdmin || isModerator;

  return (
    <Ctx.Provider value={{
      user,
      isAuthenticated: !!user,
      isAdmin,
      isSuperAdmin,
      isVenueManager: role ? BUSINESS_OWNER_ROLES.includes(role) : false,
      isBusinessOwner,
      isModerator,
      role,
      hasPermission,
      login, signup, logout,
      rolePerms,
      updateRolePermissions,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}
